import React, {useEffect, useState, useRef} from "react";
import GenericLayout from "layouts/generic";
import css from "./index.module.scss";
import Card from "@/components/card";
import ContactSection from "sections/contact";
import { useInView } from "react-intersection-observer";
import { gsap } from "scripts/gsap";
import SplitText from "@/components/splitText";
import slideFadeIn from "scripts/animations/slideFadeIn";
import popFadeIn from "scripts/animations/popFadeIn";
import inViewSettings from "scripts/animations/inViewSettings";
import TacticsSmall, {TACTICS} from "@/sections/tacticsSmall";
import Articles from "@/sections/articles";
import strapi from "scripts/strapi";
import TestimonialsSpotlight, {TESTIMONIALS} from "@/sections/testimonialsSpotlight";
import Fold1Section from "@/sections/fold1";
import MobileLoadingSvg from "/public/images/icons/mobileLoading.svg";
import Link from "next/link";
import BasicCardSection from "@/sections/basicCards";

export default function LandingPageDevelopment({articles}) {
  const [elements, setElements] = useState({
    fold: {
      table: useInView(inViewSettings((targets) => {
        let table = targets[0];
        let tl = gsap.timeline({});

        let stats = table.querySelectorAll(`.${css.stat}`);
        let bars = table.querySelectorAll(`.${css.bar}`);
        tl.fromTo(table, {
          visibility: "visible",
          opacity: 0,
        }, {
          opacity: 1,
          duration: 0.25,
          ease: "sine.inOut",
        }, 0);
        tl.fromTo(table.querySelector(`.${css.headline} svg`), {
          scaleX: 0,
          scaleY: 0,
          transformOrigin: "center center",
        }, {
          scaleX: 1,
          scaleY: 1,
          duration: 0.15,
          ease: "sine.inOut",
        });
        tl.fromTo(table.querySelector(`.${css.headline} span`), {
          opacity: 0,
          transformOrigin: "center center",
        }, {
          opacity: 1,
          duration: 0.5,
          ease: "sine.inOut",
        }, "<25%");

        tl.fromTo(stats, {
          opacity: 0,
        }, {
          opacity: 1,
          duration: 0.5,
          ease: "sine.inOut",
          stagger: {
            each: 0.125,
          },
        }, "<25%");
        tl.fromTo(bars, {
          scaleX: 0,
          transformOrigin: "center left",
        }, {
          scaleX: 1,
          duration: 0.5,
          ease: "sine.in",
          stagger: {
            each: 0.125,
          },
        }, "<0.125");
        tl.fromTo(table.querySelector(`.${css.attribution}`), {
          opacity: 0,
        }, {
          opacity: 1,
          duration: 0.5,
          ease: "sine.inOut",
        }, "<75%");
      })),
    },
    problem: {
      text: useInView(inViewSettings(slideFadeIn)),
      headline: useInView(inViewSettings(slideFadeIn)),
    },
    solution: {
      text: useInView(inViewSettings(slideFadeIn)),
      headline: useInView(inViewSettings(slideFadeIn)),
    },
  });

  return <GenericLayout
    title="Lightning Fast Landing Pages | Deviate Labs"
    description="We craft high-converting landing pages that captivate visitors and drive leads with compelling copy, intuitive design, and technical optimization."
    color="purple"
  >
    <Fold1Section
      headline={<>
        <div>Lightning-Fast Custom</div>
        <div>Engineered Landing Pages</div>
      </>}
      className={css.fold}
      subheadline="Our front-end engineers specialize in optimized, high-performance landing pages that load in the blink of an eye."
      image={
        <div className={`target ${css.table}`} ref={elements.fold.table.ref}>
          <div className={css.headline}><MobileLoadingSvg/><span>As page load time goes from:</span></div>
          <div className={`${css.row} ${css.green}`}>
            <div className={css.stat}>
              <span>1 to 3s&nbsp;</span>
              <span>the probability of bounce&nbsp;</span>
              <span>increases 32%</span>
            </div>
            <div className={css.bar} style={{"width": "26%"}}></div>
          </div>
          <div className={`${css.row} ${css.red}`}>
            <div className={css.stat}>
              <span>1 to 5s&nbsp;</span>
              <span>the probability of bounce&nbsp;</span>
              <span>increases 90%</span>
            </div>
            <div className={css.bar} style={{"width": "73%"}}></div>
          </div>
          <div className={`${css.row} ${css.red}`}>
            <div className={css.stat}>
              <span>1 to 6s&nbsp;</span>
              <span>the probability of bounce&nbsp;</span>
              <span>increases 106%</span>
            </div>
            <div className={css.bar} style={{"width": "86%"}}></div>
          </div>
          <div className={`${css.row} ${css.red}`}>
            <div className={css.stat}>
              <span>1 to 10s&nbsp;</span>
              <span>the probability of bounce&nbsp;</span>
              <span>increases 123%</span>
            </div>
            <div className={css.bar} style={{"width": "100%"}}></div>
          </div>
          <div className={css.attribution}>From <Link href="https://www.thinkwithgoogle.com/marketing-strategies/app-and-mobile/mobile-page-speed-new-industry-benchmarks/">Think With Google</Link>.</div>
        </div>
      }
    />



    <section className={`section ${css.problem}`}>
      <div className="sectionInner">
        <h2 ref={elements.problem.headline.ref}>
          <SplitText className="target" text="The Problem: Slow, Bloated Landing Pages"/>
        </h2>

        <div className={css.text} ref={elements.problem.text.ref}>
          <p>
            <SplitText className="target" text="High-performing landing pages are essential for converting visitors and driving growth. But most businesses still rely on generic page builders that generate bloated, slow-loading pages. The result? Unoptimized landing pages with:"/>
          </p>

          <ul>
            <li className="target">Slow load times from excessive images and scripts</li>
            <li className="target">Clunky user experience from autogenerated code</li>
            <li className="target">Poor conversions from lack of customization</li>
          </ul>

          <p>
            <SplitText className="target" text="These common missteps stop your potential customers — and conversions — in their tracks ."/>
          </p>
        </div>
      </div>
    </section>

    <section className={`section ${css.solution}`}>
      <div className="sectionInner">
        <h2 ref={elements.solution.headline.ref}>
          <SplitText className="target" text="The Solution: Custom Landing Pages"/>
        </h2>

        <div className={css.text} ref={elements.solution.text.ref}>
          <p>
            <SplitText className="target" text="At Deviate Labs, we build landing pages from the ground up focused on speed and performance. Our optimized development process includes:"/>
          </p>

          <ul>
            <li className="target"><b>Lightweight Custom Code:</b> <span>We hand-code pages without bulky page builders, writing clean, streamlined HTML/CSS/JavaScript.</span></li>
            <li className="target"><b>Image Optimization:</b> All images are compressed, resized for fast loading, and lazy loaded to appear only when needed.</li>
            <li className="target"><b>Minified Scripts:</b> Code files are minified and consolidated to minimize network requests.</li>
            <li className="target"><b>Google Cloud Hosting:</b> We host our custom pages on Google Cloud for blazing fast server response times.</li>
            <li className="target"><b>Mobile-First Design:</b> We design for mobile before enhancing for desktop to emphasize fast loading on all devices.</li>
            <li className="target"><b>Performance Testing:</b> We test pages using the Lighthouse suite, Google&apos;s recommendation for SEO.</li>
            <li className="target"><b>Ongoing Optimization:</b> After launch, we continuously monitor and improve page speed based on field performance.</li>
          </ul>
          <p>
            <SplitText className="target" text="The result? Landing pages that load instantly, engage visitors, and drive conversions."/>
          </p>
        </div>
      </div>
    </section>

    <BasicCardSection
      headline="Glossary: Landing Page Benchmarks"
      subheadline="Learn some of the essential terminology we’ll use to analyze and optimize your landing pages."
      width="2"
      color="cyan"
      cards={[
        {
          name: "First Contentful Paint",
          body: <>The time at which the first text appears on your landing page is its First Contentful Paint. We aim to keep this under <b>one second</b> for a rapid first impression — and lower bounce rates.</>,
        },
        {
          name: "Total Blocking Time",
          body: <>Total Blocking Time represents how long the page is unresponsive to clicks. We keep this under <b>200ms</b> to avoid laggy or frozen pages that will cause confusion for your landing page&apos;s users.</>,
        },
        {
          name: "Cumulative Layout Shift",
          body: <>Cumulative Layout Shift measures how much the elements on a page jump around as the page loads. These unpredictable jumps are annoying and off-putting for users, so we always keep CLS under <b>0.05</b>.</>,
        },
        {
          name: "Largest Contentful Paint",
          body: <>The time when the largest element on the screen is known as the page&apos;s Largest Contentful Paint. We aim for <b>2.5s</b> LCP because this has metric the biggest impact on a user&apos;s perception of load time, and a fast load time is essential to increasing the user&apos;s time on your landing page.</>,
        },

      ]}
    />


    <TestimonialsSpotlight testimonial={TESTIMONIALS.SMART_TECHNOLOGIES}/>


    <TacticsSmall enabled={[
      TACTICS.CONTENT_MARKETING,
      TACTICS.B2B_SAAS_GROWTH_MARKETING,
      TACTICS.SONIC_BRANDING,
      TACTICS.GROWTH_MARKETING,
      TACTICS.STUNT_MARKETING,
    ]}/>
    <Articles
      articles={articles}
      headline="Learn More About Page Performance"
      subheadline="Our front-end engineers are experts on website performance."
    />

    <ContactSection/>


  </GenericLayout>;
}


export async function getStaticProps() {
  let data = await strapi.article.findMany({
    filters: {
      id: {
        $eq: [33, 32, 27],
      },
    },
    populate: {
      coverArt: "*",
    },
    fields: [ //don't include the body since it's large and unnecessary
      "headline",
      "slug",
      "teaser",
      "priority",
      "category",
      "publishedAt",
    ],
  });

  return {
    props: {
      articles: data,
    },
    revalidate: 60,
  };
}
